import { useCallback } from "react";
import { useChatStore } from "@store/chatStore";
import { useFeedbackApi } from "./useFeedbackApi";
import type { MarkdownFeedbackParams } from "../types";

interface UseMarkdownFeedbackParams {
    sessionId: string;
    effectiveSessionId: string;
}

/**
 * Markdown 内容反馈处理 Hook
 * 负责 Markdown 类型的点赞/点踩/取消操作
 */
export const useMarkdownFeedback = ({ sessionId, effectiveSessionId }: UseMarkdownFeedbackParams) => {
    const updateToolStatus = useChatStore((state) => state.updateToolStatus);
    const { callFeedbackApi } = useFeedbackApi({ sessionId });

    /**
     * 处理 Markdown 点赞/点踩
     */
    const handleMarkdownFeedback = useCallback(
        async (type: "like" | "unlike", params: MarkdownFeedbackParams): Promise<{ success: boolean }> => {
            // 参数验证
            if (!params.messageId || !params.fragmentId) {
                console.error('Missing messageId or fragmentId in handleMarkdownFeedback');
                return { success: false };
            }

            const result = await callFeedbackApi(type, params);
            
            if (result.success) {
                // 更新 Store
                updateToolStatus(effectiveSessionId, params.messageId, params.fragmentId, type, 1);
            }
            
            return result;
        },
        [effectiveSessionId, updateToolStatus, callFeedbackApi]
    );

    /**
     * 处理 Markdown 取消反馈
     */
    const handleMarkdownCancel = useCallback(
        async (params: MarkdownFeedbackParams): Promise<{ success: boolean }> => {
            // 参数验证
            if (!params.messageId || !params.fragmentId) {
                console.error('Missing messageId or fragmentId in handleMarkdownCancel');
                return { success: false };
            }

            const result = await callFeedbackApi("cancel", params);
            
            if (result.success) {
                // 更新 Store：同时清除 like 和 unlike
                updateToolStatus(effectiveSessionId, params.messageId, params.fragmentId, "like", 0);
                updateToolStatus(effectiveSessionId, params.messageId, params.fragmentId, "unlike", 0);
            }
            
            return result;
        },
        [effectiveSessionId, updateToolStatus, callFeedbackApi]
    );

    return {
        handleMarkdownFeedback,
        handleMarkdownCancel,
    };
};